import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import UploadedAttachmentList from "../../components/UploadedAttachmentList";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

// 🔧 match backend constant
const DRIVER_DISCLOSURE_TASK_ID = 59;

const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "application/vnd.ms-outlook", "application/x-msg", "application/CDFV2", "application/x-ole-storage", "application/octet-stream", ""];

const normalizeDateInput = (val) => {
  if (!val) return "";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

export default function DriverDisclosureForm() {
  const navigate = useNavigate();
  const { id: paramId, ddId } = useParams();
  const isNew = !ddId || ddId === "new";

  const role = localStorage.getItem("userRole");
  const isAdmin = role === "1";
  const loggedInId = localStorage.getItem("userId");
  const userId = isAdmin && paramId ? paramId : loggedInId;

  const [form, setForm] = useState({
    dd_userid: userId,
    dd_no_violations: false,
    dd_disclosures: [{ date: "", incident: "", location: "", vehicle: "" }],
  });

  const [isSigned, setIsSigned] = useState(false);

  // attachments
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);

  // ============================
  // Load record (edit)
  // ============================
  useEffect(() => {
    const load = async () => {
      if (isNew) return;
      try {
        const res = await axios.get(
          `${API_BASE}/api/driver-disclosure/detail/${ddId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const d = res.data || {};
        setForm({
          dd_userid: d.dd_userid || userId,
          dd_no_violations: !!d.dd_no_violations,
          dd_disclosures:
            Array.isArray(d.dd_disclosures) && d.dd_disclosures.length
              ? d.dd_disclosures.map((r) => ({
                  date: normalizeDateInput(r.date),
                  incident: r.incident || "",
                  location: r.location || "",
                  vehicle: r.vehicle || "",
                }))
              : [{ date: "", incident: "", location: "", vehicle: "" }],
        });
      } catch (err) {
        console.error("❌ Driver Disclosure detail fetch error", err);
      }
    };
    load();
  }, [isNew, ddId, userId]);

  // ============================
  // Load attachments (edit)
  // ============================
  useEffect(() => {
    const loadAttachments = async () => {
      if (isNew) return;
      try {
        const res = await axios.get(
          `${API_BASE}/api/driver-disclosure/attachments/${ddId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        setExistingAttachments(res.data || []);
      } catch (err) {
        console.error("❌ Driver Disclosure attachments fetch error", err);
      }
    };
    loadAttachments();
  }, [isNew, ddId]);

  // ============================
  // Lock if signed (EXACT ADC LOGIC)
  // ============================
  useEffect(() => {
    const fetchSignStatus = async () => {
      if (!ddId || ddId === "new") return;
      try {
        const res = await axios.get(
          `${API_BASE}/api/usertasks/status/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const refKey = `${DRIVER_DISCLOSURE_TASK_ID}_${ddId}`;
        const taskKey = "driver_disclosure";

        const status =
          res.data.status?.[refKey] ??
          res.data.status?.[taskKey] ??
          "not_completed";

        setIsSigned(status === "review" || status === "completed");
      } catch (err) {
        console.error("❌ Driver Disclosure sign status fetch error", err);
      }
    };
    fetchSignStatus();
  }, [userId, ddId]);

  const disclosures = useMemo(
    () => form.dd_disclosures || [],
    [form.dd_disclosures]
  );

  const setDisclosure = (idx, key, value) => {
    const next = disclosures.map((r, i) =>
      i === idx ? { ...r, [key]: value } : r
    );
    setForm((p) => ({ ...p, dd_disclosures: next }));
  };

  const addRow = () => {
    setForm((p) => ({
      ...p,
      dd_disclosures: [
        ...(p.dd_disclosures || []),
        { date: "", incident: "", location: "", vehicle: "" },
      ],
    }));
  };

  const removeRow = (idx) => {
    const next = disclosures.filter((_, i) => i !== idx);
    setForm((p) => ({
      ...p,
      dd_disclosures: next.length
        ? next
        : [{ date: "", incident: "", location: "", vehicle: "" }],
    }));
  };

  const validateFiles = (files) => {
    const list = Array.from(files || []);
    const bad = list.find((f) => !allowedTypes.includes(f.type));
    if (bad) {
      alert("Incorrect file format.");
      return [];
    }
    return list;
  };

  // ============================
  // Save (create/update)
  // ============================
  const handleSave = async (e) => {
    e.preventDefault();

    const payload = {
      dd_userid: userId,
      dd_no_violations: !!form.dd_no_violations,
      dd_disclosures: (form.dd_disclosures || []).filter((r) => {
        return (
          (r.date || r.incident || r.location || r.vehicle)
            .toString()
            .trim() !== ""
        );
      }),
    };

    try {
      if (isNew) {
        const res = await axios.post(
          `${API_BASE}/api/driver-disclosure`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        alert("✔️ Created successfully!");
        // EXACT ADC behavior: go to detail page
        navigate(`/driver-disclosure/${userId}/detail/${res.data.id}`);
      } else {
        await axios.put(`${API_BASE}/api/driver-disclosure/${ddId}`, payload, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        alert("✔️ Updated successfully!");
      }
    } catch (err) {
      console.error("❌ Driver Disclosure save error", err);
      alert("Save failed. Please try again.");
    }
  };

  // ============================
  // Upload attachments (multi)
  // ============================
  const uploadAttachments = async () => {
    if (isNew) return alert("Please save first.");
    if (!pendingFiles.length) return;

    const fd = new FormData();
    fd.append("dd_id", ddId);
    fd.append("dd_userid", userId);
    pendingFiles.forEach((f) => fd.append("files", f));

    try {
      await axios.post(
        `${API_BASE}/api/driver-disclosure/attachments/upload`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      alert("✔️ Saved successfully!");
      setPendingFiles([]);

      const res = await axios.get(
        `${API_BASE}/api/driver-disclosure/attachments/${ddId}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      setExistingAttachments(res.data || []);
    } catch (err) {
      console.error("❌ Driver Disclosure attachment upload error", err);
      alert("Upload failed.");
    }
  };

  const deleteAttachment = async (dda_id) => {
    const attachment = existingAttachments.find((item) => item.dda_id === dda_id);
    if (
      !window.confirm(
        `Delete uploaded file "${attachment?.dda_original || attachment?.dda_file || "attachment"}"?`,
      )
    ) {
      return;
    }
    try {
      await axios.delete(
        `${API_BASE}/api/driver-disclosure/attachments/${dda_id}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      setExistingAttachments((p) => p.filter((x) => x.dda_id !== dda_id));
    } catch (err) {
      console.error("❌ Driver Disclosure attachment delete error", err);
      alert("Delete failed.");
    }
  };

  //   const goBack = () => navigate(-1);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center pt-3">
        <span style={{ fontSize: 18, fontWeight: 500 }}>
          Driver Disclosure of Convictions & At-Fault Accidents
        </span>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => navigate(`/driver-disclosure/${userId}`)}
          >
            View List
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate(-1)}
          >
            Return
          </button>
        </div>
      </div>

      <div className="form-wrapper my-4">
        <div className="text-center mb-3">
          <h3 className="mb-0">
            DRIVER DISCLOSURE OF CONVICTIONS & AT-FAULT ACCIDENTS
          </h3>
        </div>

        <div className="card p-3 mb-3">
          I hereby declare that the following is a true and complete list of
          convictions and accidents required to be reported under the Highway
          Traffic Act, Sec 318.1(3).
        </div>

        <form onSubmit={handleSave}>
          {/* Disclosures table */}
          <div className="card p-3 mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">Violations</h5>
              <button
                type="button"
                className="btn btn-outline-success btn-sm"
                onClick={addRow}
                disabled={(!isAdmin && isSigned) || form.dd_no_violations}
              >
                ➕ Add Row
              </button>
            </div>

            <div className="table-responsive">
              <table className="table table-bordered">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "15%" }}>Date</th>
                    <th>Incident</th>
                    <th style={{ width: "25%" }}>Location</th>
                    <th style={{ width: "20%" }}>Type of Vehicle Operated</th>
                    <th style={{ width: "8%" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {disclosures.map((r, idx) => (
                    <tr key={idx}>
                      <td>
                        <input
                          type="date"
                          className="form-control"
                          value={r.date || ""}
                          disabled={
                            (!isAdmin && isSigned) || form.dd_no_violations
                          }
                          onChange={(e) =>
                            setDisclosure(idx, "date", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="form-control"
                          value={r.incident || ""}
                          disabled={
                            (!isAdmin && isSigned) || form.dd_no_violations
                          }
                          onChange={(e) =>
                            setDisclosure(idx, "incident", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="form-control"
                          value={r.location || ""}
                          disabled={
                            (!isAdmin && isSigned) || form.dd_no_violations
                          }
                          onChange={(e) =>
                            setDisclosure(idx, "location", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="form-control"
                          value={r.vehicle || ""}
                          disabled={
                            (!isAdmin && isSigned) || form.dd_no_violations
                          }
                          onChange={(e) =>
                            setDisclosure(idx, "vehicle", e.target.value)
                          }
                        />
                      </td>
                      <td className="text-center">
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          disabled={
                            (!isAdmin && isSigned) || form.dd_no_violations
                          }
                          onClick={() => removeRow(idx)}
                        >
                          🗑
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="d-flex justify-content-end">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!isAdmin && isSigned}
              >
                Save
              </button>
            </div>
          </div>
          {/* No violations checkbox */}
          <div className="card p-3 mb-3">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="noViolations"
                checked={!!form.dd_no_violations}
                disabled={!isAdmin && isSigned}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    dd_no_violations: e.target.checked,
                    dd_disclosures: e.target.checked
                      ? [{ date: "", incident: "", location: "", vehicle: "" }]
                      : p.dd_disclosures,
                  }))
                }
              />
              <label className="form-check-label" htmlFor="noViolations">
                I hereby certify that I have not had any convictions (other than
                parking and mobile or intersection photo enforcement
                convictions) or accidents required to be reported under the
                Highway Traffic Act, Sec 318.1(3) that have occurred while
                operating any motor vehicle in the previous 12 months
              </label>
            </div>
          </div>

          {/* Attachments (multi upload) */}
          <div className="card p-3 mb-3">
            <h5>Attachments (multi-file)</h5>

            {isNew ? (
              <div className="alert alert-info mb-0">
                Please save the record before uploading attachments.
              </div>
            ) : (
              <>
                <div className="mb-2">
                  <label>Choose Files (PDF/JPG/PNG)</label>
                  <input
                    type="file"
                    className="form-control"
                    multiple
                    disabled={!isAdmin && isSigned}
                    accept=".pdf,.jpg,.jpeg,.png,.msg"
                    onChange={(e) => {
                      const files = validateFiles(e.target.files);
                      if (files.length)
                        setPendingFiles((p) => [...p, ...files]);
                      e.target.value = "";
                    }}
                  />
                </div>

                {pendingFiles.length > 0 && (
                  <div className="mb-3">
                    <div className="text-muted mb-1">Files Ready To Save</div>
                    <ul className="list-group">
                      {pendingFiles.map((f, idx) => (
                        <li
                          key={`${f.name}-${idx}`}
                          className="list-group-item d-flex justify-content-between align-items-center"
                        >
                          <span>{f.name}</span>
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              data-delete-permission-exempt="true"
                              disabled={!isAdmin && isSigned}
                            onClick={() =>
                              setPendingFiles((p) =>
                                p.filter((_, i) => i !== idx)
                              )
                            }
                              title="Delete"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                        </li>
                      ))}
                    </ul>

                    <div className="d-flex justify-content-end mt-2">
                      <button
                        type="button"
                        className="btn btn-success"
                        title="Save"
                        disabled={
                          !pendingFiles.length || (!isAdmin && isSigned)
                        }
                        onClick={uploadAttachments}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                )}

                <UploadedAttachmentList
                  title="Uploaded Files"
                  files={existingAttachments}
                  getFileKey={(file) => file.dda_id}
                  getFileName={(file) => file.dda_original || file.dda_file}
                  getFileViewUrl={(file) =>
                    `${API_BASE}/uploads/driver-disclosure/${file.dda_file}`
                  }
                  canDelete={Boolean(isAdmin || !isSigned)}
                  onDeleteFile={(file) => deleteAttachment(file.dda_id)}
                />
              </>
            )}
          </div>
        </form>

        {/* Signature & Review (same pattern as ADC) */}
        {!isNew && (
          <>
            <hr className="my-4" />
            <SignatureManager
              taskName="driver_disclosure"
              taskId={DRIVER_DISCLOSURE_TASK_ID}
              referenceId={ddId}
              userId={userId}
              isAdmin={isAdmin}
              // NOTE: no onSignComplete here unless your SignatureManager supports it like ADC
            />
            <ReviewManager
              taskName="driver_disclosure"
              taskId={DRIVER_DISCLOSURE_TASK_ID}
              referenceId={ddId}
              userId={userId}
              isAdmin={isAdmin}
            />
          </>
        )}
      </div>
    </>
  );
}
